/**
 * Folio how-to layer: page registry.
 *
 * Hand-maintained typed inventory of every user-facing route. The build script
 * `apps/web/scripts/extract-page-registry.mjs` exports this as
 * `packages/page-registry/registry.json`, which the backend Folio playbook
 * registry consumes at module load. Drift is gated by `pnpm run verify`.
 *
 * See `docs/architecture/folio-howto-design.md` (Tier 1).
 */

import type { PageCapability, SendablePageName } from '@/hooks/folio-page-context-contract';

/**
 * Tenant-scoped capability strings (Phase 4.2 entitlements). These mirror the
 * boolean flags on the project capabilities payload (`analyses_enabled`,
 * `verify_enabled`, …). Distinct from per-page `PageCapability`.
 */
export type TenantCapability = 'analyses_enabled' | 'verify_enabled';

/**
 * Project role required to see / use a button. Subset is intentional: the
 * registry only encodes role-gated affordances, not full RBAC.
 */
export type ProjectRole = 'viewer' | 'editor' | 'owner';

export type VisibilityPredicate =
  | { readonly kind: 'role_at_least'; readonly role: ProjectRole }
  | { readonly kind: 'tenant_capability'; readonly capability: TenantCapability }
  | { readonly kind: 'page_state'; readonly description: string };

export type EntryPoint =
  | {
      readonly kind: 'tenant_sidebar';
      readonly label: string;
      readonly testId: string;
      readonly visibleWhen?: VisibilityPredicate;
    }
  | {
      readonly kind: 'project_sidebar';
      readonly label: string;
      readonly testId: string;
      readonly visibleWhen?: VisibilityPredicate;
    }
  | {
      readonly kind: 'tab';
      readonly parentPageId: string;
      readonly queryParam: string;
      readonly queryValue: string;
      readonly label: string;
      readonly testId: string;
    }
  | {
      readonly kind: 'in_page_button';
      readonly onPageId: string;
      readonly label: string;
      readonly testId: string;
      readonly visibleWhen?: VisibilityPredicate;
    }
  | {
      readonly kind: 'modal';
      readonly openedFromPageId: string;
      readonly triggerTestId: string;
      readonly label: string;
    }
  | {
      readonly kind: 'row_action';
      readonly onPageId: string;
      readonly rowSelector: string;
      readonly menuLabel: string;
      readonly testId: string;
    }
  | {
      readonly kind: 'deep_link';
      readonly reachedFromTaskIds: readonly string[];
    };

export interface RouteShape {
  /** React Router pattern relative to BrowserRouter root, e.g. `/:tenantSlug/projects/:projectId/runs`. */
  readonly pattern: string;
  /** Tabs in this codebase live in query state; encode them here. */
  readonly requiredQuery?: Readonly<Record<string, string>>;
}

export interface RegisteredPage {
  /** Globally unique id assigned by this registry. NOT the legacy `pageContext.page` enum. */
  readonly pageId: string;
  /** Human-readable page name as the user sees it (e.g. "Verify"). */
  readonly pageName: string;
  readonly route: RouteShape;
  readonly pageCapabilities: readonly PageCapability[];
  readonly requiresTenantCapabilities: readonly TenantCapability[];
  readonly entryPaths: readonly (readonly EntryPoint[])[];
  /** Opt-out for non-task pages (login, error, redirects). PR guard requires this OR a real entryPath. */
  readonly noHowtoRequired?: { readonly reason: string };
  /**
   * The `pageContext.page` Sean works from on this route until the page
   * registers a richer context. Every page under the Sean layout needs one, or
   * the header hides the Sean button there; pages outside it (sign-in,
   * onboarding, terms) and redirects leave it out. `route-coverage.test.ts`
   * checks both against the router.
   */
  readonly folioPage?: SendablePageName;
}

const NO_TENANT_CAPS: readonly TenantCapability[] = [];
const NO_PAGE_CAPS: readonly PageCapability[] = [];
const NO_ENTRY_PATHS: readonly (readonly EntryPoint[])[] = [];

/**
 * The page name account settings is sent as. The API learns `settings` in this
 * release, but replicas of the previous one reject it, so settings goes out as
 * `admin` until that release has shipped everywhere; `page_id` and the
 * `settings_tab` metadata say which page and tab it is.
 */
export const ACCOUNT_SETTINGS_FOLIO_PAGE = 'admin' satisfies SendablePageName;

export const PAGE_REGISTRY: readonly RegisteredPage[] = [
  // ---- Auth / onboarding (no how-to needed) ----
  {
    pageId: 'login',
    pageName: 'Sign in',
    route: { pattern: '/login' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Authentication entry point.' },
  },
  {
    pageId: 'landing',
    pageName: 'Landing',
    route: { pattern: '/landing' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Public marketing page.' },
  },
  {
    pageId: 'onboarding-choice',
    pageName: 'Onboarding',
    route: { pattern: '/onboarding' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Forced onboarding flow; no in-app navigation target.' },
  },
  {
    pageId: 'onboarding-new-tenant',
    pageName: 'Request new workspace',
    route: { pattern: '/onboarding/new' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Onboarding sub-flow.' },
  },
  {
    pageId: 'onboarding-join-tenant',
    pageName: 'Request to join workspace',
    route: { pattern: '/onboarding/join' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Onboarding sub-flow.' },
  },
  {
    pageId: 'onboarding-pending',
    pageName: 'Approval pending',
    route: { pattern: '/onboarding/pending' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Onboarding hold state.' },
  },
  {
    pageId: 'terms-acceptance',
    pageName: 'Terms of service',
    route: { pattern: '/terms' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Forced terms acceptance gate.' },
  },
  {
    pageId: 'pending-invitations',
    pageName: 'Pending invitations',
    route: { pattern: '/invitations' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Holding page for users without an active workspace.' },
  },
  {
    pageId: 'cli-login-approval',
    pageName: 'Approve CLI sign-in',
    route: { pattern: '/cli-login' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Reached only from the CLI sign-in link.' },
  },
  {
    pageId: 'tenant-redirect',
    pageName: 'Workspace redirect',
    route: { pattern: '/' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Server-side redirect to active tenant.' },
  },
  {
    pageId: 'not-found',
    pageName: 'Not found',
    route: { pattern: '*' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Catch-all error page.' },
  },

  // ---- Global authenticated routes ----
  {
    pageId: 'global-settings',
    pageName: 'Account settings',
    route: { pattern: '/settings' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: {
      reason: 'Profile/account settings; not a how-to destination today.',
    },
    folioPage: ACCOUNT_SETTINGS_FOLIO_PAGE,
  },
  {
    pageId: 'admin',
    pageName: 'Platform admin',
    route: { pattern: '/admin' },
    pageCapabilities: ['tenant_administration'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Superadmin-only console; out of scope for tenant how-to.' },
    folioPage: 'admin',
  },
  {
    pageId: 'admin-workspace-create',
    pageName: 'New workspace',
    route: { pattern: '/admin/workspaces/new' },
    pageCapabilities: ['tenant_administration'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Superadmin-only console; out of scope for tenant how-to.' },
    folioPage: 'admin',
  },
  {
    pageId: 'admin-workspace-detail',
    pageName: 'Workspace detail',
    route: { pattern: '/admin/workspaces/:tenantId' },
    pageCapabilities: ['tenant_administration'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Superadmin-only console; out of scope for tenant how-to.' },
    folioPage: 'admin',
  },
  {
    pageId: 'control-plane',
    pageName: 'Central control plane',
    route: { pattern: '/control-plane' },
    pageCapabilities: ['portfolio_overview'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: NO_ENTRY_PATHS,
    noHowtoRequired: { reason: 'Read-only superadmin reporting; out of scope for tenant how-to.' },
    folioPage: 'control-plane',
  },

  // ---- Tenant-scoped routes ----
  {
    pageId: 'tenant-dashboard',
    pageName: 'Dashboard',
    route: { pattern: '/:tenantSlug' },
    pageCapabilities: ['portfolio_overview', 'setup_guidance'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Dashboard', testId: 'tenant-sidebar-dashboard-link' }],
    ],
    folioPage: 'dashboard',
  },
  {
    pageId: 'tenant-projects',
    pageName: 'Projects',
    route: { pattern: '/:tenantSlug/projects' },
    pageCapabilities: ['portfolio_overview'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Projects', testId: 'tenant-sidebar-projects-link' }],
    ],
    folioPage: 'dashboard',
  },
  {
    pageId: 'tenant-documents',
    pageName: 'Documents',
    route: { pattern: '/:tenantSlug/documents' },
    pageCapabilities: ['document_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Documents', testId: 'tenant-sidebar-documents-link' }],
    ],
    folioPage: 'documents',
  },
  {
    pageId: 'tenant-templates',
    pageName: 'Templates',
    route: { pattern: '/:tenantSlug/templates' },
    pageCapabilities: ['template_catalog', 'template_creation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Templates', testId: 'tenant-sidebar-templates-link' }],
    ],
    folioPage: 'templates',
  },
  {
    pageId: 'template-editor-new',
    pageName: 'New template',
    route: { pattern: '/:tenantSlug/templates/new' },
    pageCapabilities: ['template_creation', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Templates', testId: 'tenant-sidebar-templates-link' }],
    ],
    folioPage: 'template-editor',
  },
  {
    pageId: 'template-detail',
    pageName: 'Template detail',
    route: { pattern: '/:tenantSlug/templates/:templateId' },
    pageCapabilities: ['template_review', 'template_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'deep_link', reachedFromTaskIds: [] }],
    ],
    folioPage: 'template-detail',
  },
  {
    pageId: 'template-editor-edit',
    pageName: 'Edit template',
    route: { pattern: '/:tenantSlug/templates/:templateId/edit' },
    pageCapabilities: ['template_iteration', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'deep_link', reachedFromTaskIds: [] }],
    ],
    folioPage: 'template-editor',
  },
  {
    pageId: 'tenant-members',
    pageName: 'Members',
    route: { pattern: '/:tenantSlug/members' },
    pageCapabilities: ['member_management'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Members', testId: 'tenant-sidebar-members-link' }],
    ],
    folioPage: 'members',
  },
  {
    pageId: 'tenant-project-access',
    pageName: 'Project access',
    route: { pattern: '/:tenantSlug/project-access' },
    pageCapabilities: ['member_management'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'tenant_sidebar',
          label: 'Project access',
          testId: 'tenant-sidebar-project-access-link',
        },
      ],
    ],
    folioPage: 'members',
  },
  {
    pageId: 'tenant-settings',
    pageName: 'Account settings',
    route: { pattern: '/:tenantSlug/settings' },
    pageCapabilities: NO_PAGE_CAPS,
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'tenant_sidebar', label: 'Settings', testId: 'tenant-sidebar-settings-link' }],
    ],
    folioPage: ACCOUNT_SETTINGS_FOLIO_PAGE,
  },

  // ---- Project-scoped routes ----
  {
    pageId: 'project-detail',
    pageName: 'Project overview',
    route: { pattern: '/:tenantSlug/projects/:projectId' },
    pageCapabilities: ['project_overview'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'project_sidebar', label: 'Overview', testId: 'project-sidebar-overview-link' }],
    ],
    folioPage: 'project-detail',
  },
  {
    pageId: 'project-tsa-workspace',
    pageName: 'TSA Workspace',
    route: { pattern: '/:tenantSlug/projects/:projectId/tsa-workspace' },
    pageCapabilities: ['project_overview', 'workspace_assistance', 'tsa_workspace'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'TSA Workspace',
          testId: 'project-sidebar-tsa-workspace-link',
        },
      ],
    ],
    folioPage: 'project-detail',
  },
  {
    pageId: 'project-documents',
    pageName: 'Documents and template results',
    route: { pattern: '/:tenantSlug/projects/:projectId/documents' },
    pageCapabilities: [
      'document_navigation',
      'analysis_review',
      'workspace_assistance',
      'citation_navigation',
    ],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [{ kind: 'project_sidebar', label: 'Documents', testId: 'project-sidebar-documents-link' }],
    ],
    folioPage: 'documents',
  },
  {
    pageId: 'project-document-detail',
    pageName: 'Document detail',
    route: { pattern: '/:tenantSlug/projects/:projectId/documents/:documentId' },
    pageCapabilities: ['result_review', 'citation_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        { kind: 'project_sidebar', label: 'Documents', testId: 'project-sidebar-documents-link' },
        {
          kind: 'row_action',
          onPageId: 'project-documents',
          rowSelector: '[data-testid="documents-table-row"]',
          menuLabel: 'Open document',
          testId: 'documents-table-filename-link',
        },
      ],
    ],
    folioPage: 'document-detail',
  },
  {
    pageId: 'evaluations',
    pageName: 'Verify',
    route: {
      pattern: '/:tenantSlug/projects/:projectId/evaluations',
      requiredQuery: { tab: 'evaluations' },
    },
    pageCapabilities: ['evaluation_overview'],
    requiresTenantCapabilities: ['verify_enabled'],
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Verify',
          testId: 'project-sidebar-verify-link',
          visibleWhen: { kind: 'tenant_capability', capability: 'verify_enabled' },
        },
        {
          kind: 'tab',
          parentPageId: 'ground-truth',
          queryParam: 'tab',
          queryValue: 'evaluations',
          label: 'Evaluations',
          testId: 'evaluations-tab-evaluations',
        },
      ],
    ],
    folioPage: 'evaluations',
  },
  {
    pageId: 'ground-truth',
    pageName: 'Verify',
    route: {
      pattern: '/:tenantSlug/projects/:projectId/evaluations',
      requiredQuery: { tab: 'ground-truth' },
    },
    pageCapabilities: ['ground_truth_review', 'ground_truth_navigation'],
    requiresTenantCapabilities: ['verify_enabled'],
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Verify',
          testId: 'project-sidebar-verify-link',
          visibleWhen: { kind: 'tenant_capability', capability: 'verify_enabled' },
        },
        {
          kind: 'tab',
          parentPageId: 'evaluations',
          queryParam: 'tab',
          queryValue: 'ground-truth',
          label: 'Ground Truth',
          testId: 'evaluations-tab-ground-truth',
        },
      ],
    ],
    folioPage: 'ground-truth',
  },
  {
    pageId: 'project-eval-comparison',
    pageName: 'Compare evaluations',
    route: { pattern: '/:tenantSlug/projects/:projectId/evaluations/compare' },
    pageCapabilities: ['evaluation_comparison'],
    requiresTenantCapabilities: ['verify_enabled'],
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: ['compare_runs'] }]],
    folioPage: 'evaluation-compare',
  },
  {
    pageId: 'project-eval-run-detail',
    pageName: 'Evaluation detail',
    route: { pattern: '/:tenantSlug/projects/:projectId/evaluations/:evalRunId' },
    pageCapabilities: ['evaluation_review'],
    requiresTenantCapabilities: ['verify_enabled'],
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'evaluation-detail',
  },
  {
    pageId: 'project-ground-truth-annotate',
    pageName: 'Annotate ground truth',
    route: {
      pattern: '/:tenantSlug/projects/:projectId/ground-truth/annotate/:documentId',
    },
    pageCapabilities: ['ground_truth_annotation'],
    requiresTenantCapabilities: ['verify_enabled'],
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Verify',
          testId: 'project-sidebar-verify-link',
          visibleWhen: { kind: 'tenant_capability', capability: 'verify_enabled' },
        },
        {
          kind: 'tab',
          parentPageId: 'evaluations',
          queryParam: 'tab',
          queryValue: 'ground-truth',
          label: 'Ground Truth',
          testId: 'evaluations-tab-ground-truth',
        },
        {
          kind: 'row_action',
          onPageId: 'ground-truth',
          rowSelector: 'ground-truth-row',
          menuLabel: 'Annotate',
          testId: 'ground-truth-annotate-row-button',
        },
      ],
    ],
    folioPage: 'ground-truth-annotate',
  },
  {
    pageId: 'project-templates',
    pageName: 'Project templates',
    route: { pattern: '/:tenantSlug/projects/:projectId/templates' },
    pageCapabilities: ['template_catalog', 'template_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Templates',
          testId: 'project-sidebar-templates-link',
        },
      ],
    ],
    folioPage: 'templates',
  },
  {
    pageId: 'project-template-detail',
    pageName: 'Template detail',
    route: { pattern: '/:tenantSlug/projects/:projectId/templates/:templateId' },
    pageCapabilities: ['template_review', 'template_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-detail',
  },
  {
    pageId: 'project-template-editor-new',
    pageName: 'New template',
    route: { pattern: '/:tenantSlug/projects/:projectId/templates/new' },
    pageCapabilities: ['template_creation', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-editor',
  },
  {
    pageId: 'project-template-editor-edit',
    pageName: 'Edit template',
    route: { pattern: '/:tenantSlug/projects/:projectId/templates/:templateId/edit' },
    pageCapabilities: ['template_iteration', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-editor',
  },
  {
    pageId: 'project-members',
    pageName: 'Project members',
    route: { pattern: '/:tenantSlug/projects/:projectId/members' },
    pageCapabilities: ['member_management'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Members',
          testId: 'project-sidebar-members-link',
        },
      ],
    ],
    folioPage: 'members',
  },
  {
    pageId: 'project-settings',
    pageName: 'Project settings',
    route: { pattern: '/:tenantSlug/projects/:projectId/settings' },
    pageCapabilities: ['project_configuration', 'run_overview'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Project settings',
          testId: 'project-sidebar-project-settings-link',
          visibleWhen: { kind: 'role_at_least', role: 'editor' },
        },
      ],
    ],
    folioPage: 'project-detail',
  },
  {
    pageId: 'project-upload',
    pageName: 'Upload redirect',
    route: { pattern: '/:tenantSlug/projects/:projectId/upload' },
    pageCapabilities: ['document_upload'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [],
    noHowtoRequired: {
      reason: 'Legacy upload entry point retained for deep links; superseded by upload-documents.',
    },
  },
  {
    pageId: 'project-upload-documents',
    pageName: 'Upload documents',
    route: { pattern: '/:tenantSlug/projects/:projectId/upload-documents' },
    pageCapabilities: ['document_upload'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [
      [
        {
          kind: 'project_sidebar',
          label: 'Documents',
          testId: 'project-sidebar-documents-link',
        },
        {
          kind: 'in_page_button',
          onPageId: 'project-documents',
          label: 'Upload documents',
          testId: 'project-documents-upload-documents-button',
          visibleWhen: { kind: 'role_at_least', role: 'editor' },
        },
      ],
    ],
    folioPage: 'document-upload',
  },
  {
    pageId: 'tsa-template-detail',
    pageName: 'TSA template detail',
    route: { pattern: '/:tenantSlug/projects/:projectId/tsa-workspace/templates/:templateId' },
    pageCapabilities: ['template_review', 'template_navigation'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-detail',
  },
  {
    pageId: 'tsa-template-editor-new',
    pageName: 'New TSA template',
    route: { pattern: '/:tenantSlug/projects/:projectId/tsa-workspace/templates/new' },
    pageCapabilities: ['template_creation', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-editor',
  },
  {
    pageId: 'tsa-template-editor-edit',
    pageName: 'Edit TSA template',
    route: {
      pattern: '/:tenantSlug/projects/:projectId/tsa-workspace/templates/:templateId/edit',
    },
    pageCapabilities: ['template_iteration', 'template_authoring', 'template_schema_editing'],
    requiresTenantCapabilities: NO_TENANT_CAPS,
    entryPaths: [[{ kind: 'deep_link', reachedFromTaskIds: [] }]],
    folioPage: 'template-editor',
  },
];

// ---- Helpers --------------------------------------------------------------

const PAGE_REGISTRY_BY_ID: ReadonlyMap<string, RegisteredPage> = new Map(
  PAGE_REGISTRY.map((page) => [page.pageId, page]),
);

export function getRegisteredPage(pageId: string): RegisteredPage | undefined {
  return PAGE_REGISTRY_BY_ID.get(pageId);
}

/**
 * Best-effort lookup by route pattern + query params. Used by tests/build script
 * to assert that every React Router pattern is in the registry. Matches require
 * an exact pattern match AND that all `requiredQuery` entries are satisfied by
 * the supplied `queryParams`.
 */
export function getRegisteredPageByRoute(
  routePattern: string,
  queryParams: Readonly<Record<string, string>> = {},
): RegisteredPage | undefined {
  return PAGE_REGISTRY.find((page) => {
    if (page.route.pattern !== routePattern) {
      return false;
    }
    const required = page.route.requiredQuery;
    if (required === undefined) {
      return true;
    }
    return Object.entries(required).every(([key, value]) => queryParams[key] === value);
  });
}

// Sanity check at module load: page IDs must be globally unique.
{
  const seen = new Set<string>();
  for (const page of PAGE_REGISTRY) {
    if (seen.has(page.pageId)) {
      throw new Error(`Duplicate Folio pageId: ${page.pageId}`);
    }
    seen.add(page.pageId);
  }
}
